<script setup lang="ts">
import { ref, watch, onUnmounted } from 'vue';
import Step1 from './steps/Step1.vue';
import Step2 from './steps/Step2.vue';
import Step3 from './steps/Step3.vue';
import Step4 from './steps/Step4.vue';

interface EditingStep {
  title: string;
  desc: string;
  view: 'main' | 'add-more' | 'add-market' | 'alter-market';
}

const props = defineProps<{
  activeStep: number;
  steps: EditingStep[];
}>();

const emit = defineEmits<{
  (e: 'update:activeStep', step: number): void;
}>();

const typedSearch = ref("");
const showResults = ref(false);
const isTyping = ref(false);
let typeInterval: any = null;

const startTyping = () => {
  typedSearch.value = "";
  showResults.value = false;
  isTyping.value = true;
  const targetText = "Chelsea";
  let i = 0;
  
  if (typeInterval) clearInterval(typeInterval);
  
  typeInterval = setInterval(() => {
    if (i < targetText.length) {
      typedSearch.value += targetText[i];
      i++;
    } else {
      clearInterval(typeInterval);
      isTyping.value = false;
      setTimeout(() => {
        showResults.value = true;
      }, 500);
    }
  }, 100);
};

const isDropdownOpen = ref(false);
const isMarketDropdownOpen = ref(false);
const isMarketPickerOpen = ref(false);
const isHeaderMenuOpen = ref(false);
const showHand = ref(false);
const handPos = ref({ x: 0, y: 0 });

const startDropdownAnimation = (type: 'market' | 'period' | 'picker' | 'header') => {
  if (type === 'market') {
    isMarketDropdownOpen.value = false;
    handPos.value = { x: 80, y: 50 };
    showHand.value = true;
  } else if (type === 'period') {
    isDropdownOpen.value = false;
    showHand.value = false;
    setTimeout(() => { isDropdownOpen.value = true; }, 1000);
    return;
  } else if (type === 'picker') {
    isMarketPickerOpen.value = false;
    handPos.value = { x: 80, y: 80 };
    showHand.value = true;
  } else if (type === 'header') {
    isHeaderMenuOpen.value = false;
    showHand.value = false;
    setTimeout(() => {
      isHeaderMenuOpen.value = true;
    }, 1000); 
    return;
  }
  
  setTimeout(() => {
    if (type === 'market') {
      handPos.value = { x: 50, y: 25 }; 
    } else if (type === 'picker') {
      handPos.value = { x: 50, y: 30 }; 
    }
    
    setTimeout(() => {
      if (type === 'market') {
        isMarketDropdownOpen.value = true;
      } else if (type === 'picker') {
        isMarketPickerOpen.value = true;
      }
      
      setTimeout(() => {
        showHand.value = false;
      }, 500);
    }, 600);
  }, 400);
};

watch(() => props.activeStep, (newStep) => {
  const stepView = props.steps[newStep]?.view;
  
  isDropdownOpen.value = false;
  isMarketDropdownOpen.value = false;
  isMarketPickerOpen.value = false;
  isHeaderMenuOpen.value = false;
  showHand.value = false;
  
  if (stepView === 'add-more') {
    startTyping();
  } else if (stepView === 'main' && newStep === 0) {
    startDropdownAnimation('header');
  } else if (stepView === 'alter-market' && newStep === 1) {
    startDropdownAnimation('header');
    startDropdownAnimation('period');
  } else if (stepView === 'add-market') {
    startDropdownAnimation('picker');
  } else if (stepView === 'alter-market') {
    startDropdownAnimation('period');
  } else {
    typedSearch.value = "";
    showResults.value = false;
  }
}, { immediate: true });

onUnmounted(() => {
  if (typeInterval) clearInterval(typeInterval);
});
</script>

<template>
  <div class="app-mock-container glass no-padding overflow-hidden bg-black">
    <transition name="fade-in" mode="out-in">
      <Step1 
        v-if="activeStep === 0" 
        :activeStep="activeStep"
        :isHeaderMenuOpen="isHeaderMenuOpen"
        :showHand="showHand"
        :handPos="handPos"
        @update:activeStep="emit('update:activeStep', $event)"
      />
      <Step2
        v-else-if="activeStep === 1"
        :activeStep="activeStep"
        :isHeaderMenuOpen="isHeaderMenuOpen"
        :isDropdownOpen="isDropdownOpen"
        :showHand="showHand"
        :handPos="handPos"
        @update:activeStep="emit('update:activeStep', $event)"
      />
      <Step3
        v-else-if="activeStep === 2"
        :typedSearch="typedSearch"
        :isTyping="isTyping"
        :showResults="showResults"
      />
      <Step4
        v-else-if="activeStep === 3"
        :isMarketPickerOpen="isMarketPickerOpen"
      />
    </transition>
  </div>
</template>

<style scoped>
.app-mock-container {
  width: 100%;
  max-width: 800px;
  height: 600px;
  background: #000;
  border-radius: 32px;
  border: 1px solid rgba(255,255,255,0.1);
  box-shadow: 0 40px 100px rgba(0,0,0,0.5);
  position: relative;
  box-sizing: border-box;
}
.app-mock-container.no-padding { padding: 0; }

@media (max-width: 768px) {
  .app-mock-container { border-radius: 0; min-height: 500px; }
}

.fade-in-enter-active { animation: fadeIn 0.4s ease; }
.fade-in-leave-active { animation: fadeOut 0.3s ease; }
@keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
@keyframes fadeOut { from { opacity: 1; } to { opacity: 0; } }
</style>
